import { useMsal } from '@azure/msal-react';
import React, { useState } from 'react';
import { getCompressedJson } from '../../API/Export/ExportApi';
import { checkProjectForDisplayPages, generateIncrementalChanges, generatePageConfiguration, removeAllChanges, removePlaceholderLinks, removePortalCreatedPages, removePortalItems, validateForPublish } from '../../API/Generation/Generation';
import { publishApplication, replacePublishErrorTerms, updateProjectPortalConfig } from '../../Components/Helpers/Launch';
import { updateProjectPropertiesAfterPublish } from '../../API/Project/project';

function usePublishApplication(projectId, organizationId, instance, user, isFirstTimePublish = false) {

    const [publishState, setPublishState] = useState("");
    const [error, setError] = useState("");

    const msalContext = useMsal();

    function getCurrentUserId() {
        if (msalContext.accounts.length === 0)
            return user.azureId;
        if (msalContext.accounts.length > 0)
            return msalContext.accounts[0].localAccountId;
        throw new Error("User is not logged in to designer");
    }

    async function publishApp() {
        try {
            setPublishState("InProgress");
            setError("");
            let result;
            const projectHasDisplayPages = await checkProjectForDisplayPages(projectId);
            if (projectHasDisplayPages) {
                result = await performIncrementalGeneration();
            } else {
                result = await performFullGeneration();
            }
            return result;
        } catch (e) {
            setPublishState("Failure");
            setError(replacePublishErrorTerms(e.message));
        }
    }

    async function validateApp() {
        const response = await validateForPublish(projectId, "production");
        if (response.error) {
            throw new Error(response.error);
        }
    }

    async function performFullGeneration() {
        try {
            setPublishState("InProgress");
            await validateApp();
            await generatePageForFullPublish();
            const compressedJson = await getCompressedJson(
                projectId, organizationId, getCurrentUserId(), instance._id, instance.instanceType !== "Production", false
            );
            updateProjectPortalConfig(projectId);
            const result = await publishApplication(msalContext, instance, compressedJson, isFirstTimePublish);
            if (result.status !== "Success") {
                throw result
            };
            await updateProjectPropertiesAfterPublish(projectId, instance._id);
            setPublishState("Success");
            return result;
        } catch (e) {
            setPublishState("Failure");
            setError(replacePublishErrorTerms(e.message));
            return e;
        }
    }

    async function generatePageForFullPublish() {
        await removePortalCreatedPages(projectId);
        await removePortalItems(projectId);

        await generatePageConfiguration(projectId);

        await removePlaceholderLinks(projectId);

        await removeAllChanges(projectId);
    }

    async function performIncrementalGeneration() {
        try {
            setPublishState("InProgress");
            await validateApp();
            const [compressedJson, generationError] = await generateIncrementalChanges(
                projectId, organizationId, getCurrentUserId(), instance._id, instance.instanceType !== "Production", false
            );
            if (generationError) {
                throw new Error(generationError);
            }
            await removeAllChanges(projectId);
            updateProjectPortalConfig(projectId);
            const result = await publishApplication(msalContext, instance, compressedJson, isFirstTimePublish);
            if (result.status !== "Success") {
                throw new Error(result.message);
            }
            await updateProjectPropertiesAfterPublish(projectId, instance._id);
            setPublishState("Success");
            return result;
        } catch (e) {
            setPublishState("Failure");
            setError(replacePublishErrorTerms(e.message));
        }
    }

    return [
        publishState,
        error,
        publishApp,
        performFullGeneration,
        performIncrementalGeneration
    ];
}

export default usePublishApplication;